import React, { useState, useRef, useEffect } from 'react';
import { i18n } from '@international/mastodon-i18n';
import { CANDIDATE_CONTEXT_MAX_LENGTH } from './utils/constants';
import {
  CandidateContextTextareaProps,
  CandidateContextFieldProps,
  CandidateContextEditorProps,
} from './utils/interfaces';

// EVERI-4005: own module (no SimpleDocumentList/UploadButton/SimplePanel) so AiDocReview.tsx's import doesn't drag in DocumentVerificationComponents.tsx's heavier deps.

const CANDIDATE_CONTEXT_I18N_PREFIX =
  'components.Verification.EmploymentMultidocPanel.candidateContext';

// Shared textarea + counter, used by CandidateContextField and CandidateContextEditor.
// Considered MultilineText (same char-counter textarea, already used for the explanation box) but it's a redux-form Field, and this value is plain local/lifted state, not a form field.
const CandidateContextTextarea: React.FC<CandidateContextTextareaProps> = ({
  id,
  value,
  onChange,
  testId,
  ariaLabel,
}) => (
  <>
    <textarea
      id={id}
      aria-label={ariaLabel}
      className='candidate-context__input'
      data-testid={testId}
      rows={2}
      maxLength={CANDIDATE_CONTEXT_MAX_LENGTH}
      value={value}
      placeholder={i18n.getStr(`${CANDIDATE_CONTEXT_I18N_PREFIX}.placeholder`)}
      // EVERI-4005: `maxLength` only blocks native typing/pasting — a value set programmatically (autofill, some IME/dictation paths) skips it entirely, so clamp here too rather than trust the attribute alone.
      onChange={event =>
        onChange(event.target.value.slice(0, CANDIDATE_CONTEXT_MAX_LENGTH))
      }
    />
    <span
      className='candidate-context__counter'
      data-testid={`${testId}-counter`}
    >
      {`${value.length}/${CANDIDATE_CONTEXT_MAX_LENGTH}`}
    </span>
  </>
);

// Always open — used in the review modal on an already-flagged document.
const CandidateContextField: React.FC<CandidateContextFieldProps> = ({
  id,
  value,
  onChange,
}) => (
  <div className='candidate-context'>
    <label className='candidate-context__label' htmlFor={id}>
      {i18n.getStr(`${CANDIDATE_CONTEXT_I18N_PREFIX}.label`)}
    </label>
    <CandidateContextTextarea
      id={id}
      value={value}
      onChange={onChange}
      testId='candidate-context-input'
    />
  </div>
);

// Collapsed quoted note + Edit (or "Add context" when empty); Cancel discards, only Save commits.
const CandidateContextEditor: React.FC<CandidateContextEditorProps> = ({
  id,
  value,
  onSave,
  onEditingChange = () => {},
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // EVERI-4005: mirrors the latest isEditing/onEditingChange for the unmount cleanup below, without re-running that effect on every render.
  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;

  const setEditing = (editing: boolean) => {
    setIsEditing(editing);
    onEditingChange(editing);
  };

  // EVERI-4005: removing a row (or the panel unmounting) while this editor is open must still release the count — Cancel/Save aren't the only ways it can go away.
  useEffect(
    () => () => {
      if (isEditingRef.current) onEditingChangeRef.current(false);
    },
    [],
  );

  // EVERI-4005: the ref is set synchronously (not just mirrored on render) so a same-tick double-fire (e.g. a fast double-click) can't slip past this check before React re-renders — each fire would otherwise increment openContextEditorCount, and a single Cancel/Save would only decrement it once.
  const openEditor = () => {
    if (isEditingRef.current) return;
    isEditingRef.current = true;
    setDraft(value);
    setEditing(true);
  };

  // EVERI-4005: same same-tick double-fire guard as openEditor, so a fast double-click can't decrement openContextEditorCount twice for one close.
  const cancel = () => {
    if (!isEditingRef.current) return;
    isEditingRef.current = false;
    setDraft(value);
    setEditing(false);
  };

  const save = () => {
    if (!isEditingRef.current) return;
    isEditingRef.current = false;
    onSave(draft.trim());
    setEditing(false);
  };

  if (isEditing) {
    return (
      <div className='candidate-context candidate-context--editing'>
        <CandidateContextTextarea
          id={id}
          value={draft}
          onChange={setDraft}
          testId='candidate-context-editor-input'
          ariaLabel={i18n.getStr(`${CANDIDATE_CONTEXT_I18N_PREFIX}.label`)}
        />
        <div className='candidate-context__actions'>
          <button
            type='button'
            className='candidate-context__cancel'
            data-testid='candidate-context-cancel'
            onClick={cancel}
          >
            {i18n.getStr('buttons.cancel')}
          </button>
          <button
            type='button'
            className='candidate-context__save'
            data-testid='candidate-context-save'
            onClick={save}
          >
            {i18n.getStr('buttons.save')}
          </button>
        </div>
      </div>
    );
  }

  if (value) {
    return (
      <div className='candidate-context candidate-context--saved'>
        <span className='candidate-context__value'>{value}</span>
        <button
          type='button'
          className='candidate-context__edit'
          data-testid='candidate-context-edit'
          onClick={openEditor}
        >
          {i18n.getStr('buttons.edit')}
        </button>
      </div>
    );
  }

  return (
    <button
      type='button'
      className='candidate-context__add'
      data-testid='candidate-context-add'
      onClick={openEditor}
    >
      {i18n.getStr(`${CANDIDATE_CONTEXT_I18N_PREFIX}.add`)}
    </button>
  );
};

export {
  CandidateContextTextarea,
  CandidateContextField,
  CandidateContextEditor,
};
